'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useComposerContext } from "./internals/ComposerContext.js";
import { matchesMimeType } from "./internals/matchesMimeType.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ComposerAttachButton = /*#__PURE__*/React.forwardRef(function ComposerAttachButton(props, ref) {
  const {
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const composer = useComposerContext();
  const localeText = useChatLocaleText();
  const inputRef = React.useRef(null);
  const ownerState = {
    isSubmitting: composer.isSubmitting,
    hasValue: composer.hasValue,
    isStreaming: composer.isStreaming,
    attachmentCount: composer.attachmentCount,
    disabled: composer.disabled
  };
  const AttachButton = slots?.attachButton ?? 'button';
  const AttachInput = slots?.attachInput ?? 'input';
  const rootProps = useSlotProps({
    elementType: AttachButton,
    externalSlotProps: slotProps?.attachButton,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref
    }
  });
  const {
    attachmentConfig
  } = composer;
  const acceptAttr = attachmentConfig?.acceptedMimeTypes?.join(',') || undefined;
  const inputProps = useSlotProps({
    elementType: AttachInput,
    externalSlotProps: slotProps?.attachInput,
    ownerState,
    additionalProps: {
      'aria-label': localeText.composerAttachInputLabel,
      hidden: true,
      multiple: true,
      ref: inputRef,
      type: 'file',
      accept: acceptAttr
    }
  });
  const externalOnClick = rootProps.onClick;
  const externalOnChange = inputProps.onChange;
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsx(AttachInput, _extends({}, inputProps, {
      onChange: event => {
        externalOnChange?.(event);
        if (event.defaultPrevented) {
          return;
        }
        const files = Array.from(event.currentTarget.files ?? []);
        const rejections = [];
        const accepted = [];
        const currentCount = composer.attachmentCount;
        for (const file of files) {
          if (attachmentConfig?.acceptedMimeTypes && attachmentConfig.acceptedMimeTypes.length > 0 && !matchesMimeType(file.type, attachmentConfig.acceptedMimeTypes)) {
            rejections.push({
              file,
              reason: 'mime-type'
            });
            continue;
          }
          if (attachmentConfig?.maxFileSize != null && file.size > attachmentConfig.maxFileSize) {
            rejections.push({
              file,
              reason: 'file-size'
            });
            continue;
          }
          if (attachmentConfig?.maxFileCount != null && currentCount + accepted.length >= attachmentConfig.maxFileCount) {
            rejections.push({
              file,
              reason: 'file-count'
            });
            continue;
          }
          accepted.push(file);
        }
        if (rejections.length > 0) {
          attachmentConfig?.onAttachmentReject?.(rejections);
        }
        for (const file of accepted) {
          composer.addAttachment(file);
        }
        event.currentTarget.value = '';
      }
    })), /*#__PURE__*/_jsx(AttachButton, _extends({}, rootProps, {
      "aria-label": rootProps['aria-label'] ?? localeText.composerAttachButtonLabel,
      disabled: Boolean(rootProps.disabled) || ownerState.disabled,
      onClick: event => {
        externalOnClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        inputRef.current?.click();
      },
      type: rootProps.type ?? 'button'
    }))]
  });
});
if (process.env.NODE_ENV !== "production") ComposerAttachButton.displayName = "ComposerAttachButton";